import React, { useState, useEffect } from 'react';
import { BarChart2, DollarSign, TrendingUp, Settings, RefreshCw } from 'lucide-react';
import WalletSelector from '../../components/WalletSelector';
import { getWalletInfo, getWalletProfitAndLoss } from '../../services/solanatrackerService';

interface WalletStatsProps {
  onClose?: () => void;
  isWidget?: boolean;
}

const WalletStats: React.FC<WalletStatsProps> = ({
  onClose,
  isWidget = false
}) => {
  const [selectedWallet, setSelectedWallet] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [walletData, setWalletData] = useState<any>(null);
  const [pnlData, setPnlData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch wallet data when a wallet is selected
  useEffect(() => {
    const fetchWalletData = async () => {
      if (!selectedWallet) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch wallet info and profit/loss data in parallel
        const [walletInfo, profitLoss] = await Promise.all([
          getWalletInfo(selectedWallet),
          getWalletProfitAndLoss(selectedWallet)
        ]);
        
        setWalletData(walletInfo);
        setPnlData(profitLoss);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        setError('Failed to load wallet data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchWalletData();
  }, [selectedWallet]);
  
  // Helper function to format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Format percentages with + sign and colors
  const formatPercentage = (num: number) => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };
  
  // Format currency values
  const formatCurrency = (num: number) => {
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    } else {
      return `$${num.toFixed(2)}`;
    }
  };
  
  // Format SOL values
  const formatSol = (num: number) => {
    return `${num.toFixed(4)} SOL`;
  };
  
  // The main content of the widget
  const content = (
    <div className="w-full h-full flex flex-col bg-transparent" data-widget-type="wallet-stats">
      {/* Header with wallet selector */}
      <div className="p-3 border-b border-white/10">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-white flex items-center">
            <BarChart2 size={16} className="mr-2 text-blue-400" /> 
            Wallet Statistics
          </h3>
          
          <div className="flex items-center gap-2">
            {!isLoading && selectedWallet && (
              <button 
                onClick={() => {
                  setIsLoading(true);
                  // Simulate refreshing the data
                  setTimeout(() => {
                    setIsLoading(false);
                  }, 1000);
                }} 
                className="p-1.5 rounded-full text-gray-400 hover:text-white transition-colors"
              >
                <RefreshCw size={14} />
              </button>
            )}
            <button className="p-1.5 rounded-full text-gray-400 hover:text-white transition-colors">
              <Settings size={14} />
            </button>
          </div>
        </div>
        
        <div className="mb-3">
          <WalletSelector 
            onSelectWallet={setSelectedWallet}
            selectedWalletAddress={selectedWallet}
          />
        </div>
      </div>
      
      {/* Wallet stats content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-400"></div>
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-400 p-4 text-center">
            {error}
          </div>
        ) : !selectedWallet ? (
          <div className="h-full flex items-center justify-center text-gray-400 p-4 text-center">
            Please select a wallet to view statistics
          </div>
        ) : !walletData || !pnlData ? (
          <div className="h-full flex items-center justify-center text-gray-400 p-4 text-center">
            No data available for this wallet
          </div>
        ) : (
          <div className="p-3">
            {/* Overview cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-400 mb-1">Total Value</div>
                <div className="text-lg font-semibold text-white">{formatCurrency(walletData.total)}</div>
                <div className="flex items-center mt-1">
                  <DollarSign size={12} className="text-blue-400 mr-1" />
                  <span className="text-xs text-gray-300">{formatSol(walletData.totalSol)}</span>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                <div className="text-xs text-gray-400 mb-1">Total P&L</div>
                <div className={`text-lg font-semibold ${pnlData.summary.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(pnlData.summary.total)}
                </div>
                <div className="flex items-center mt-1">
                  <TrendingUp size={12} className="text-blue-400 mr-1" />
                  <span className={`text-xs ${pnlData.summary.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ROI: {formatPercentage((pnlData.summary.total / pnlData.summary.totalInvested) * 100)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Performance metrics */}
            <div className="mb-4">
              <h4 className="text-xs font-medium text-gray-400 mb-2">Performance Metrics</h4>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-gray-400">Win Rate</div>
                    <div className="text-sm text-green-400">{pnlData.summary.winPercentage.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Loss Rate</div>
                    <div className="text-sm text-red-400">{pnlData.summary.lossPercentage.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Total Wins</div>
                    <div className="text-sm text-white">{pnlData.summary.totalWins}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Total Losses</div>
                    <div className="text-sm text-white">{pnlData.summary.totalLosses}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Financial breakdown */}
            <div className="mb-4">
              <h4 className="text-xs font-medium text-gray-400 mb-2">Financial Breakdown</h4>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                <div className="grid grid-cols-2 gap-y-3">
                  <div>
                    <div className="text-xs text-gray-400">Realized P&L</div>
                    <div className={`text-sm ${pnlData.summary.realized >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(pnlData.summary.realized)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Unrealized P&L</div>
                    <div className={`text-sm ${pnlData.summary.unrealized >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(pnlData.summary.unrealized)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Total Invested</div>
                    <div className="text-sm text-white">{formatCurrency(pnlData.summary.totalInvested)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Avg Buy Amount</div>
                    <div className="text-sm text-white">{formatCurrency(pnlData.summary.averageBuyAmount)}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Top Positions */}
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2">Top Positions</h4>
              {Object.entries(pnlData.tokens).length > 0 ? (
                <div className="bg-slate-800/50 rounded-lg border border-white/5 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-black/20">
                      <tr>
                        <th className="text-left p-2">Token</th>
                        <th className="text-right p-2">P&L</th>
                        <th className="text-right p-2">ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(pnlData.tokens)
                        .sort(([, a]: [string, any], [, b]: [string, any]) => b.total - a.total)
                        .slice(0, 5)
                        .map(([address, token]: [string, any]) => (
                          <tr key={address} className="border-t border-white/5 hover:bg-white/5">
                            <td className="p-2 truncate max-w-[120px]">
                              {address.substring(0, 6)}...{address.substring(address.length - 4)}
                            </td>
                            <td className={`p-2 text-right ${token.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(token.total)}
                            </td>
                            <td className={`p-2 text-right ${token.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatPercentage((token.total / token.total_invested) * 100)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5 text-center text-gray-400 text-xs">
                  No token positions found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t border-white/10 text-xs text-gray-400 text-center">
        Data provided by Solana Tracker API
      </div>
    </div>
  );
  
  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }
  
  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart2 size={18} className="text-blue-400" />
          <h2 className="text-lg font-bold text-white">Wallet Statistics</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create the widget
export const createWalletStatsWidget = () => {
  return {
    type: 'wallet-stats',
    title: 'Wallet Statistics',
    content: <WalletStats isWidget={true} />,
    size: { width: 400, height: 600 },
    padding: 0,
    margin: 0,
    contentStyle: { 
      padding: 0, 
      margin: 0,
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    disablePadding: true,
    fullWidth: false,
    fullHeight: true,
    background: 'transparent',
    glassMorphism: true,
    containerClassName: 'p-0 m-0 overflow-hidden',
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default WalletStats; 